import React, { useMemo } from 'react';
import type { WizardStepNumber, WizardStepState } from './wizardTypes';
import { STEP_LABELS, ALL_STEPS } from './wizardTypes';

interface WizardStepIndicatorProps {
  steps: Record<WizardStepNumber, WizardStepState>;
  focusedStep?: WizardStepNumber;
  onStepClick?: (step: WizardStepNumber) => void;
  /** When false, step 3 (Gamification) is hidden and we show a 3-step flow */
  gamificationEnabled?: boolean;
  /** Hide step 4 (Certification) — plans with no certificates at all. */
  certificationEnabled?: boolean;
  /** Optional override for step labels (defaults to course-creation labels). */
  labels?: Record<WizardStepNumber, string>;
}

/** Checkmark icon for completed steps */
function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 13l4 4L19 7" className="stroke-primary-500" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Glass-morphism ring for the current/focused step */
function GlassRing() {
  return (
    <div
      className="absolute inset-0 rounded-full"
      style={{
        background: 'radial-gradient(circle, rgb(var(--color-primary) / 0.08) 40%, rgb(var(--color-primary) / 0.25) 70%, rgb(var(--color-primary) / 0.15) 100%)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        border: '2px solid rgb(var(--color-primary) / 0.3)',
        boxShadow: '0 0 12px rgb(var(--color-primary) / 0.15), inset 0 0 8px rgba(255,255,255,0.2)',
      }}
    />
  );
}

// Position presets: evenly spaced for 3 or 4 steps
const POSITIONS_4 = [4, 34, 64, 94];
const POSITIONS_3 = [4, 48, 94];
const POSITIONS_2 = [4, 94];

export default function WizardStepIndicator({
  steps,
  focusedStep = 1,
  onStepClick,
  gamificationEnabled = true,
  certificationEnabled = true,
  labels = STEP_LABELS,
}: WizardStepIndicatorProps) {
  // Visible steps: plans without a feature don't get its step in the bar
  // (gamification below Pro, certification on Free) — the wizard shows only
  // steps the plan can actually complete.
  const visibleSteps: WizardStepNumber[] = useMemo(
    () => ALL_STEPS.filter(
      (s) => (s !== 3 || gamificationEnabled) && (s !== 4 || certificationEnabled)
    ),
    [gamificationEnabled, certificationEnabled]
  );

  const positions =
    visibleSteps.length === 2
      ? POSITIONS_2
      : visibleSteps.length === 3
        ? POSITIONS_3
        : POSITIONS_4;

  const focusedVisibleIndex = visibleSteps.indexOf(focusedStep);
  const savedCount = visibleSteps.filter((s) => steps[s].status === 'saved').length;
  const lastSavedIndex = savedCount - 1;
  const fillToIndex = Math.max(lastSavedIndex, focusedVisibleIndex);
  const allCompleted = savedCount === visibleSteps.length;
  const fillPercent = allCompleted ? 100 : fillToIndex < 0 ? 2 : positions[fillToIndex] + 2;

  return (
    <div className="sticky top-[72px] z-30 overflow-hidden bg-background-primary">
      <div className="max-w-[890px] mx-auto px-6 pt-4 relative">

        {/* Track bar */}
        <div className="relative h-8">
          {/* Background track — unfilled: primary-100 */}
          <div className="absolute inset-0 h-8 rounded-full bg-primary-100 dark:bg-primary-900/40" />

          {/* Filled bar — primary-500 solid */}
          <div
            className="absolute top-0 left-0 h-8 rounded-full"
            style={{
              width: `${fillPercent}%`,
              background: 'rgb(var(--color-primary))',
              transition: 'width 400ms cubic-bezier(0.23, 1, 0.32, 1)',
            }}
          />

          {/* Step circles */}
          {visibleSteps.map((step, i) => {
            const { status } = steps[step];
            const isSaved = status === 'saved';
            const isFocused = step === focusedStep;
            const isPending = !isSaved && !isFocused;
            const canClick = (isSaved || status === 'active') && !!onStepClick;

            return (
              <button
                key={step}
                type="button"
                onClick={canClick ? () => onStepClick!(step) : undefined}
                disabled={!canClick}
                aria-current={isFocused ? 'step' : undefined}
                className={`absolute z-[2] -translate-x-1/2 -translate-y-1/2 ${canClick ? 'active:scale-90' : ''}`}
                style={{
                  left: `${positions[i]}%`,
                  top: '50%',
                  width: isFocused ? '45px' : '24px',
                  height: isFocused ? '45px' : '24px',
                  cursor: canClick ? 'pointer' : 'default',
                  transition:
                    'width 200ms cubic-bezier(0.23, 1, 0.32, 1), height 200ms cubic-bezier(0.23, 1, 0.32, 1), transform 150ms cubic-bezier(0.23, 1, 0.32, 1)',
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  transformOrigin: 'center',
                }}
              >
                {isFocused && (
                  <>
                    <GlassRing />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-grey-800 dark:bg-grey-200" />
                    </div>
                  </>
                )}

                {isSaved && !isFocused && (
                  <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-[0_1px_4px_rgb(var(--color-primary) / 0.2)]">
                    <CheckIcon />
                  </div>
                )}

                {isPending && (
                  <div className="w-6 h-6 rounded-full bg-grey-800 dark:bg-grey-200 shadow-[0_1px_4px_rgba(0,0,0,0.15)] dark:shadow-[0_1px_4px_rgba(255,255,255,0.18)]" />
                )}
              </button>
            );
          })}
        </div>

        {/* Labels row */}
        <div className="flex justify-between mt-2.5 pb-3">
          {visibleSteps.map((step, i) => {
            const isSaved = steps[step].status === 'saved';
            const isFocused = step === focusedStep;
            const isActive = isSaved || isFocused;
            const isFirst = i === 0;
            const isLast = i === visibleSteps.length - 1;

            return (
              <div
                key={step}
                style={{
                  width: `${100 / visibleSteps.length}%`,
                  textAlign: isFirst ? 'left' : isLast ? 'right' : 'center',
                }}
              >
                <p className={`m-0 text-xl leading-[30px] font-bold ${
                  isActive ? 'text-primary-500' : 'text-text-primary'
                }`}>
                  STEP {isFirst ? 1 : isLast ? visibleSteps.length : i + 1}
                </p>
                <p className={`m-0 text-[17px] leading-[30px] font-light ${
                  isActive ? 'text-primary-500' : 'text-text-primary'
                }`}>
                  {labels[step]}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
